import { makeRng } from '../../utils/makeRng';
import { isWalkable } from '../map/isWalkable';
import {
  SPIDER_BOSS_ENEMY_ID,
  SPIDER_BOSS_SIGHT_DIAMETER,
  SPIDERWEB_COUNT,
  isSpiderBossFloor,
} from './spiderBossConfig';

const SPIDER_BOSS_FORBIDDEN_ARENA_TILES = new Set([
  'corridor',
  'hidden-corridor',
  'secret-entrance',
  'door-open',
  'door-locked',
  'enemy',
  'enemy-boss',
  'steps-down',
]);

function clampCount(value, max) {
  return Math.max(0, Math.min(max, Math.floor(Number(value) || 0)));
}

export function coordKey(x, y) {
  return `${x},${y}`;
}

export function parseCoordKey(key) {
  if (typeof key !== 'string') return null;
  const [xRaw, yRaw] = key.split(',');
  const x = Math.floor(Number(xRaw));
  const y = Math.floor(Number(yRaw));
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

export function createDisabledSpiderBossState() {
  return {
    enabled: false,
    floor: null,
    webKeys: new Set(),
    webShakeTicksByKey: {},
    bossSpawned: false,
    bossPos: null,
    bossUnderTile: 'floor',
    bossAlertWebKey: null,
    bossLockedOnPlayer: false,
    bossDefeated: false,
  };
}

export function serializeSpiderBossState(value) {
  if (!value || typeof value !== 'object') return null;

  return {
    ...value,
    webKeys: value.webKeys instanceof Set ? [...value.webKeys] : [],
    webShakeTicksByKey:
      value.webShakeTicksByKey && typeof value.webShakeTicksByKey === 'object'
        ? value.webShakeTicksByKey
        : {},
    bossPos:
      value.bossPos && typeof value.bossPos === 'object'
        ? { x: Number(value.bossPos.x) || 0, y: Number(value.bossPos.y) || 0 }
        : null,
  };
}

export function hydrateSpiderBossState(value) {
  if (!value || typeof value !== 'object') return null;

  return {
    ...createDisabledSpiderBossState(),
    ...value,
    enabled: !!value.enabled,
    floor: Number.isFinite(Number(value.floor)) ? Math.floor(Number(value.floor)) : null,
    webKeys: new Set(Array.isArray(value.webKeys) ? value.webKeys.filter((v) => typeof v === 'string') : []),
    webShakeTicksByKey:
      value.webShakeTicksByKey && typeof value.webShakeTicksByKey === 'object'
        ? value.webShakeTicksByKey
        : {},
    bossSpawned: !!value.bossSpawned,
    bossPos:
      value.bossPos && typeof value.bossPos === 'object'
        ? {
            x: Math.floor(Number(value.bossPos.x) || 0),
            y: Math.floor(Number(value.bossPos.y) || 0),
          }
        : null,
    bossUnderTile:
      typeof value.bossUnderTile === 'string' ? value.bossUnderTile : 'floor',
    bossAlertWebKey:
      typeof value.bossAlertWebKey === 'string' ? value.bossAlertWebKey : null,
    bossLockedOnPlayer: !!value.bossLockedOnPlayer,
    bossDefeated: !!value.bossDefeated,
  };
}

function isSpiderwebEligibleTile(tile) {
  if (!isWalkable(tile)) return false;
  if (tile === 'steps-down' || tile === 'steps-up') return false;
  if (tile === 'merchant') return false;
  if (tile === 'enemy' || tile === 'enemy-boss') return false;
  if (tile === 'hidden-corridor' || tile === 'secret-entrance') return false;
  return true;
}

function isSpiderBossTraversableTile(tile) {
  if (!isWalkable(tile)) return false;
  if (tile === 'steps-up' || tile === 'steps-down') return false;
  if (tile === 'hidden-corridor' || tile === 'secret-entrance') return false;
  if (tile === 'enemy') return false;
  return true;
}

function getWebTargetCount(count, candidatesLength) {
  return clampCount(count, candidatesLength);
}

export function sanitizeSpiderBossFloorMap(map) {
  if (!Array.isArray(map) || map.length === 0) return map;

  const height = map.length;
  const width = map[0]?.length || 0;
  if (width <= 0) return map;

  let changed = false;
  const next = map.map((row) => row.slice());

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const tile = next[y]?.[x];
      const isBoundary = x === 0 || y === 0 || x === width - 1 || y === height - 1;
      let replacement = tile;

      if (isBoundary) {
        replacement = tile === 'steps-up' ? 'steps-up' : 'non-walkable';
      } else if (
        tile === 'steps-up' ||
        (isWalkable(tile) && !SPIDER_BOSS_FORBIDDEN_ARENA_TILES.has(tile))
      ) {
        replacement = tile;
      } else {
        replacement = 'floor';
      }

      if (replacement !== tile) {
        next[y][x] = replacement;
        changed = true;
      }
    }
  }

  return changed ? next : map;
}

export function buildSpiderBossFloorState({
  map,
  floor,
  playerPos,
  runSeed,
  webCount = SPIDERWEB_COUNT,
}) {
  if (!isSpiderBossFloor(floor) || !Array.isArray(map) || !playerPos) {
    return createDisabledSpiderBossState();
  }

  const candidates = [];

  for (let y = 0; y < map.length; y++) {
    for (let x = 0; x < map[y].length; x++) {
      if (!isSpiderwebEligibleTile(map[y][x])) continue;
      if (Math.max(Math.abs(x - playerPos.x), Math.abs(y - playerPos.y)) <= 1) continue;
      candidates.push({ x, y });
    }
  }

  const rng = makeRng(
    ((Number(runSeed) || 0) ^ ((Math.floor(Number(floor) || 1) * 0x9e3779b9) >>> 0) ^ 0x5b1d5eed) >>> 0
  );

  const shuffled = candidates.slice();
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  const webKeys = new Set(
    shuffled
      .slice(0, getWebTargetCount(webCount, shuffled.length))
      .map((p) => coordKey(p.x, p.y))
  );

  return {
    enabled: true,
    floor: Math.floor(Number(floor) || 1),
    webKeys,
    webShakeTicksByKey: {},
    bossSpawned: false,
    bossPos: null,
    bossUnderTile: 'floor',
    bossAlertWebKey: null,
    bossLockedOnPlayer: false,
    bossDefeated: false,
  };
}

export function getTriggeredSpiderwebKeys(webKeys, playerPos) {
  if (!(webKeys instanceof Set) || !playerPos) return [];

  const hits = [];

  webKeys.forEach((key) => {
    const pos = parseCoordKey(key);
    if (!pos) return;
    const dist = Math.max(
      Math.abs(pos.x - playerPos.x),
      Math.abs(pos.y - playerPos.y),
    );
    if (dist <= 1) hits.push(key);
  });

  hits.sort((a, b) => {
    const pa = parseCoordKey(a);
    const pb = parseCoordKey(b);
    const da = Math.abs(pa.x - playerPos.x) + Math.abs(pa.y - playerPos.y);
    const db = Math.abs(pb.x - playerPos.x) + Math.abs(pb.y - playerPos.y);
    if (da !== db) return da - db;
    return a.localeCompare(b);
  });

  return hits;
}

export function bumpSpiderwebShakeTicks(previousTicks, keys) {
  const next = {
    ...(previousTicks && typeof previousTicks === 'object' ? previousTicks : {}),
  };

  for (const key of keys) {
    next[key] = (Number(next[key]) || 0) + 1;
  }

  return next;
}

export function pickSpiderBossAlertWebKey(keys, playerPos) {
  if (!Array.isArray(keys) || keys.length === 0) return null;
  if (!playerPos) return keys[0] ?? null;

  return keys
    .slice()
    .sort((a, b) => {
      const pa = parseCoordKey(a);
      const pb = parseCoordKey(b);
      const da = Math.abs(pa.x - playerPos.x) + Math.abs(pa.y - playerPos.y);
      const db = Math.abs(pb.x - playerPos.x) + Math.abs(pb.y - playerPos.y);
      if (da !== db) return da - db;
      return a.localeCompare(b);
    })[0] ?? null;
}

export function chooseSpiderBossSpawnPosition(map, playerPos) {
  if (!Array.isArray(map) || !playerPos) return null;

  const height = map.length;
  const width = map[0]?.length || 0;
  const candidates = [];

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const tile = map[y]?.[x];
      if (!isSpiderBossTraversableTile(tile)) continue;
      if (x === playerPos.x && y === playerPos.y) continue;

      const dist = Math.abs(x - playerPos.x) + Math.abs(y - playerPos.y);
      const isOuterRing = x <= 1 || y <= 1 || x >= width - 2 || y >= height - 2;

      candidates.push({ x, y, dist, isOuterRing });
    }
  }

  candidates.sort((a, b) => {
    if (a.dist !== b.dist) return b.dist - a.dist;
    if (a.isOuterRing !== b.isOuterRing) return Number(b.isOuterRing) - Number(a.isOuterRing);
    if (a.y !== b.y) return a.y - b.y;
    return a.x - b.x;
  });

  return candidates[0]
    ? { x: candidates[0].x, y: candidates[0].y }
    : null;
}

export function findSpiderBossPos(map) {
  if (!Array.isArray(map)) return null;

  for (let y = 0; y < map.length; y++) {
    for (let x = 0; x < map[y].length; x++) {
      if (map[y][x] === 'enemy-boss') return { x, y };
    }
  }

  return null;
}

export function isPlayerWithinSpiderBossSight(bossPos, playerPos) {
  if (!bossPos || !playerPos) return false;
  const radius = Math.max(0, Math.floor((SPIDER_BOSS_SIGHT_DIAMETER - 1) / 2));
  return (
    Math.max(
      Math.abs(bossPos.x - playerPos.x),
      Math.abs(bossPos.y - playerPos.y),
    ) <= radius
  );
}

function buildPathToTarget({ map, start, goal }) {
  if (!Array.isArray(map) || !start || !goal) return null;

  const height = map.length;
  const width = map[0]?.length || 0;
  const inBounds = (x, y) => x >= 0 && y >= 0 && x < width && y < height;
  const dirs = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ];
  const startKey = coordKey(start.x, start.y);
  const goalKey = coordKey(goal.x, goal.y);
  const queue = [{ x: start.x, y: start.y }];
  const parents = new Map([[startKey, null]]);
  let head = 0;

  while (head < queue.length) {
    const current = queue[head++];
    const currentKey = coordKey(current.x, current.y);
    if (currentKey === goalKey) break;

    for (const [dx, dy] of dirs) {
      const nx = current.x + dx;
      const ny = current.y + dy;
      if (!inBounds(nx, ny)) continue;

      const nextKey = coordKey(nx, ny);
      if (parents.has(nextKey)) continue;

      const nextTile = map[ny]?.[nx];
      if (nextKey !== goalKey && !isSpiderBossTraversableTile(nextTile)) continue;

      parents.set(nextKey, currentKey);
      queue.push({ x: nx, y: ny });
    }
  }

  if (!parents.has(goalKey)) return null;

  const reversed = [];
  let currentKey = goalKey;
  while (currentKey) {
    const pos = parseCoordKey(currentKey);
    if (!pos) break;
    reversed.push(pos);
    currentKey = parents.get(currentKey) || null;
  }

  return reversed.reverse();
}

export function moveSpiderBossOneStep({
  map,
  bossPos,
  bossUnderTile,
  targetPos,
  playerPos,
}) {
  if (!Array.isArray(map) || !bossPos || !targetPos) {
    return {
      moved: false,
      map,
      bossPos,
      bossUnderTile,
      contactPlayer: false,
    };
  }

  const path = buildPathToTarget({
    map,
    start: bossPos,
    goal: targetPos,
  });

  if (!Array.isArray(path) || path.length < 2) {
    return {
      moved: false,
      map,
      bossPos,
      bossUnderTile,
      contactPlayer: false,
    };
  }

  const nextStep = path[1];
  const touchesPlayer =
    !!playerPos &&
    nextStep.x === playerPos.x &&
    nextStep.y === playerPos.y;

  if (touchesPlayer) {
    return {
      moved: false,
      map,
      bossPos,
      bossUnderTile,
      contactPlayer: true,
    };
  }

  const next = map.map((row) => row.slice());
  next[bossPos.y][bossPos.x] = bossUnderTile || 'floor';

  const nextUnderTile = next[nextStep.y]?.[nextStep.x] || 'floor';
  next[nextStep.y][nextStep.x] = 'enemy-boss';

  return {
    moved: true,
    map: next,
    bossPos: { x: nextStep.x, y: nextStep.y },
    bossUnderTile: nextUnderTile,
    contactPlayer: false,
  };
}

export { SPIDER_BOSS_ENEMY_ID };
